/**
 * El muestreador y la máquina de estados de la ficha.
 *
 * Los cuatro estados son los de docs/03 §4.3. El muestreo toma una lectura
 * cada 30 s Y en cada `visibilitychange` (ADR-016 §3): iOS Safari suspende
 * los timers al bloquearse la pantalla, y sin la muestra extra alguien que
 * estuvo los cuatro minutos llega con tres muestras.
 *
 * Lo que se calcula aquí es SOLO para la UI. El veredicto lo emite el
 * servidor recalculando desde las muestras crudas.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MIN_SAMPLES,
  SAMPLE_INTERVAL_MS,
  type LocationSample,
  type PlaceKind,
} from '@gu/contracts';

import * as api from '../../shared/lib/api.js';
import { readPosition } from '../../shared/lib/geolocation.js';

export type EstadoVisita =
  | 'sin_claim'
  | 'en_el_lugar'
  | 'llave_activa'
  | 'llave_expirada';

export interface LugarParaClaim {
  id: string;
  lat: number;
  lng: number;
  kind: PlaceKind;
}

export function useVisitClaim(lugar: LugarParaClaim) {
  const [estado, setEstado] = useState<EstadoVisita>('sin_claim');
  const [muestras, setMuestras] = useState<LocationSample[]>([]);
  const [tokenId, setTokenId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const claim = useRef<api.ClaimAbierto | null>(null);
  const t0 = useRef<number>(0);

  /**
   * Las muestras también en un ref, además del estado.
   *
   * El estado es lo que pinta la pantalla; el ref es lo que se ENVÍA. Sin él,
   * el cierre mandaría el array que estuviera vivo cuando se montó el efecto
   * —posiblemente con menos muestras de las que hay, incluso por debajo de
   * MIN_SAMPLES— y el rechazo lo habría causado el cliente, no el usuario.
   * Es un fallo que no se ve: la visita sale mal sin que nada parezca roto.
   */
  const muestrasRef = useRef<LocationSample[]>([]);

  /**
   * Un cierre ya en marcha. Un segundo `closeVisit` gasta el nonce, y el
   * servidor contestaría `replay_detected` a una visita honesta.
   *
   * Es la SEGUNDA línea de defensa: la primera es que el efecto del dwell no
   * dependa de `muestras` y por tanto no se remonte con cada lectura. Con
   * cualquiera de las dos basta —quitar solo una deja los tests en verde— y
   * están las dos a propósito, porque la primera es fácil de perder sin
   * darse cuenta: basta con que alguien añada `muestras` al array de
   * dependencias para "arreglar" un aviso del linter de hooks.
   */
  const cerrando = useRef(false);

  const tomarMuestra = useCallback(async () => {
    const r = await readPosition();
    // Un fallo de GPS no rompe nada: se salta esta muestra y sigue. Dentro de
    // un local el sensor puede callarse un rato y volver.
    if (!r.ok) return;
    const m: LocationSample = {
      lat: r.value.lat,
      lng: r.value.lng,
      accuracyM: r.value.accuracyM,
      monotonicMs: performance.now(),
    };
    muestrasRef.current = [...muestrasRef.current, m];
    setMuestras(muestrasRef.current);
  }, []);

  const empezar = useCallback(async () => {
    setError(null);
    try {
      const abierto = await api.startVisit(lugar.id);
      claim.current = abierto;
      t0.current = Date.parse(abierto.startedAt);
      cerrando.current = false;
      setEstado('en_el_lugar');
      await tomarMuestra();
    } catch (e) {
      // `request` lanza ApiError con el {code, message} del servidor: el copy
      // de rechazo de docs/09 §2.3 viaja ahí y hay que mostrarlo tal cual, no
      // sustituirlo por un genérico.
      setError(mensajeDe(e, 'No pudimos empezar tu visita.'));
    }
  }, [lugar.id, tomarMuestra]);

  // ─── Muestreo periódico ───
  useEffect(() => {
    if (estado !== 'en_el_lugar') return;
    const id = setInterval(() => void tomarMuestra(), SAMPLE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [estado, tomarMuestra]);

  // ─── Muestreo al volver al frente (ADR-016 §3) ───
  useEffect(() => {
    if (estado !== 'en_el_lugar') return;
    const alVolver = () => {
      if (document.visibilityState !== 'hidden') void tomarMuestra();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [estado, tomarMuestra]);

  // ─── Cierre automático al cumplirse el dwell ───
  //
  // El efecto NO depende de `muestras`: leerlas del ref evita que se
  // desmonte y remonte con cada lectura del GPS, que es justo cuando una
  // carrera podría colar un segundo cierre.
  useEffect(() => {
    if (estado !== 'en_el_lugar' || !claim.current) return;

    const id = setInterval(() => {
      const transcurrido = (Date.now() - t0.current) / 1000;
      if (transcurrido < claim.current!.profile.minDwellSec) return;
      if (cerrando.current) return;

      /**
       * Y con muestras suficientes. Encontrado abriendo el navegador el 18
       * de agosto, no por un test.
       *
       * Al REANUDAR un claim que ya cumplió el dwell —la app volvió a primer
       * plano (ADR-016 §2), o la persona recargó— el cliente arranca con UNA
       * muestra y el reloj ya vencido, así que dispararía el cierre de
       * inmediato. El servidor exige MIN_SAMPLES y responde
       * `visit_not_verified`… quemando el nonce.
       *
       * Es el peor rechazo posible: le pasa a quien SÍ estuvo los minutos,
       * le dice "no pudimos confirmar tu ubicación" y le obliga a empezar de
       * cero. El cliente no pide un veredicto que sabe que no puede ganar.
       */
      if (muestrasRef.current.length < MIN_SAMPLES) return;

      cerrando.current = true;
      clearInterval(id);

      void (async () => {
        try {
          const token = await api.closeVisit({
            claimId: claim.current!.claimId,
            nonce: claim.current!.nonce,
            samples: muestrasRef.current,
          });
          setTokenId(token.tokenId);
          setExpiresAt(new Date(token.expiresAt));
          setEstado('llave_activa');
        } catch (e) {
          // El rechazo NO borra muestras ni claim: el usuario debe poder
          // reintentar sin volver a empezar de cero. `cerrando` se queda en
          // true a propósito — el nonce ya se consumió y reintentar en
          // automático solo daría `replay_detected`. Reintentar es una
          // decisión de la persona, con un botón.
          setError(mensajeDe(e, 'No pudimos confirmar tu visita.'));
        }
      })();
    }, 1_000);

    return () => clearInterval(id);
  }, [estado]);

  return { estado, muestras, tokenId, expiresAt, error, empezar, setEstado };
}

/**
 * El mensaje del servidor, o uno genérico.
 *
 * Los copys de rechazo de docs/09 §2.3 son específicos a propósito —"aquí
 * adentro la señal está difícil" le dice a la persona qué hacer; "algo salió
 * mal" no— así que se muestran tal cual llegan.
 */
function mensajeDe(e: unknown, porDefecto: string): string {
  if (e instanceof api.ApiError) return e.body.message as string;
  return porDefecto;
}
